import { esc, escH, stars, uploadWithProgress, createProgressBar, showConfirm } from '../lib/helpers.js';

(() => {
  const data = window.__PAGE_DATA;
  if (!data) return;

  const AUTH = data.authApiUrl || '';
  const PLACE_ID = data.placeId;
  const BIZ_NAME = data.bizName;
  const BIZ_CITY = data.bizCity;
  const BIZ_STATE = data.bizState;
  const BIZ_SLUG = data.bizSlug;
  const BIZ_LOGO = data.bizLogo;
  const BIZ_ADDRESS = data.bizAddress;
  const BIZ_PHONE = data.bizPhone;
  const BIZ_WEBSITE = data.bizWebsite;
  const BIZ_DESCRIPTION = data.bizDescription;
  let authUser = null;
  let isOwner = false;
  const devToken = localStorage.getItem('ps_dev_token');
  const opts = { credentials: 'include' };
  const trackClaim = (event, metadata = {}) => {
    const payload = {
      event,
      place_id: PLACE_ID,
      business_name: BIZ_NAME,
      claim_id: metadata.claim_id,
      metadata: { source: 'business_page', ...metadata, claim_id: undefined },
    };
    const headers = { 'Content-Type': 'application/json' };
    if (devToken) headers.Authorization = `Bearer ${devToken}`;
    fetch('/api/claim-events', {
      method: 'POST',
      headers,
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => {});
    if (typeof window.gtag === 'function') window.gtag('event', event, payload.metadata);
  };

  const claimErrorCategory = (error) => {
    const msg = String(error || '').toLowerCase();
    if (msg.includes('already')) return 'already_claimed';
    if (msg.includes('too many')) return 'rate_limited';
    if (msg.includes('network')) return 'network';
    if (msg.includes('email')) return 'email_verification';
    if (msg.includes('code')) return 'phone_code';
    return 'unknown';
  };

  // Dev auth: inject Bearer token for localhost
  const _origFetch = window.fetch;
  if (devToken) {
    window.fetch = (url, o) => {
      o = o || {};
      if (typeof url === 'string' && url.startsWith(AUTH)) {
        o.headers = o.headers || {};
        o.headers['Authorization'] = `Bearer ${devToken}`;
      }
      return _origFetch.call(window, url, o);
    };
  }

  // Check verified status + show claim button (runs for all visitors)
  fetch(`${AUTH}/auth/claims/check?place_id=${encodeURIComponent(PLACE_ID)}`, opts)
    .then(r => r.json())
    .then(d => {
      if (d.claimed) {
        const badge = document.getElementById('verified-badge');
        const badgeSm = document.getElementById('verified-badge-sm');
        if (badge) badge.style.display = 'inline-flex';
        if (badgeSm) badgeSm.style.display = 'inline-flex';
      }
      const btn = document.getElementById('claim-btn');
      const label = document.getElementById('claim-label');
      if (btn && label) {
        if (d.claimed && !d.is_owner) {
          // Already claimed by someone else
        } else if (d.is_owner) {
          label.textContent = 'Verified owner';
          btn.classList.add('text-green-600', 'border-green-300', 'bg-green-50');
          btn.style.display = '';
        } else if (d.user_claim && d.user_claim.status === 'pending') {
          const hasDomain = d.user_claim.verify_domain || d.domain;
          label.textContent = hasDomain ? 'Verify ownership' : 'Claim pending';
          btn.classList.add('text-amber-600', 'border-amber-300', 'bg-amber-50');
          btn.disabled = !hasDomain;
          btn.style.display = '';
        } else {
          btn.style.display = '';
        }
      }
    });

  // Check auth
  fetch(`${AUTH}/auth/session/verify`, opts)
    .then(r => r.json())
    .then(d => {
      if (d.ok && d.user) {
        authUser = d.user;
        initFavorite();
        initClaim(() => { showOwnerBanner(); });
        initReviews();
      } else {
        const lp = document.getElementById('review-login-prompt');
        const rf = document.getElementById('review-form');
        const wr = document.getElementById('write-review');
        if (lp && rf && wr) { lp.classList.remove('hidden'); rf.style.display = 'none'; wr.style.display = ''; }
        const btn = document.getElementById('claim-btn');
        if (btn) {
          btn.addEventListener('click', () => {
            trackClaim('claim_requires_login');
            window.location.href = `/account/?return=${encodeURIComponent(location.pathname + location.search)}`;
          });
        }
      }
      loadCommunityReviews();
    })
    .catch(() => {
      loadCommunityReviews();
    });

  // ── Favorites ──
  const initFavorite = () => {
    const btns = [document.getElementById('fav-btn'), document.getElementById('fav-btn-sm')].filter(Boolean);
    if (!btns.length) return;
    btns.forEach(b => { b.style.display = ''; });
    fetch(`${AUTH}/auth/favorites?place_id=${encodeURIComponent(PLACE_ID)}`, opts)
      .then(r => r.json())
      .then(d => { setFavState(d.favorited); });

    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        const isFav = btn.dataset.fav === '1';
        // Optimistic — update UI immediately, fire-and-forget the request
        setFavState(!isFav);
        if (isFav) {
          fetch(`${AUTH}/auth/favorites/${encodeURIComponent(PLACE_ID)}`, Object.assign({ method: 'DELETE' }, opts))
            .catch(() => { setFavState(true); }); // revert on failure
        } else {
          fetch(`${AUTH}/auth/favorites`, Object.assign({
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ place_id: PLACE_ID, business_name: BIZ_NAME, city: BIZ_CITY, state: BIZ_STATE, slug: BIZ_SLUG, logo_url: BIZ_LOGO, address: BIZ_ADDRESS, phone: BIZ_PHONE })
          }, opts)).catch(() => { setFavState(false); }); // revert on failure
        }
      });
    });
  };

  const setFavState = (isFav) => {
    const btns = [document.getElementById('fav-btn'), document.getElementById('fav-btn-sm')].filter(Boolean);
    btns.forEach(btn => {
      const icon = btn.querySelector('.fav-icon') || document.getElementById('fav-icon');
      btn.dataset.fav = isFav ? '1' : '0';
      if (isFav) {
        if (icon) icon.setAttribute('fill', 'currentColor');
        btn.classList.remove('text-red-300'); btn.classList.add('text-red-500');
        btn.classList.remove('border-slate-300'); btn.classList.add('border-red-300');
      } else {
        if (icon) icon.setAttribute('fill', 'none');
        btn.classList.add('text-red-300'); btn.classList.remove('text-red-500');
        btn.classList.add('border-slate-300'); btn.classList.remove('border-red-300');
      }
    });
  };

  // ── Claims ──
  const initClaim = (onDone) => {
    const btn = document.getElementById('claim-btn');
    const label = document.getElementById('claim-label');
    if (!btn || !label) { if (onDone) onDone(); return; }
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);
    const newLabel = document.getElementById('claim-label');

    fetch(`${AUTH}/auth/claims/check?place_id=${encodeURIComponent(PLACE_ID)}`, opts)
      .then(r => r.json())
      .then(d => {
        if (d.is_owner) {
          isOwner = true;
        } else if (d.user_claim && d.user_claim.status === 'pending') {
          const domain = d.user_claim.verify_domain || d.domain;
          const cEmail = d.user_claim.contact_email || d.contact_email;
          const cPhone = d.phone;
          if (domain || cEmail || cPhone) {
            newBtn.disabled = false;
            newBtn.addEventListener('click', () => {
              trackClaim('claim_verify_open', { claim_id: d.user_claim.id, has_email: Boolean(domain || cEmail), has_phone: Boolean(cPhone) });
              showVerifyModal(d.user_claim.id, domain, cEmail, cPhone);
            });
          }
        } else if (!d.claimed) {
          newBtn.addEventListener('click', () => {
            trackClaim('claim_click');
            newBtn.disabled = true; newLabel.textContent = 'Claiming...';
            fetch(`${AUTH}/auth/claims`, Object.assign({
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ place_id: PLACE_ID, business_name: BIZ_NAME })
            }, opts))
              .then(r => r.json())
              .then(res => {
                if (res.ok) {
                  if (res.status === 'approved') {
                    newLabel.textContent = 'Verified owner';
                    newBtn.classList.add('text-green-600', 'border-green-300', 'bg-green-50');
                    isOwner = true;
                    initOwnerPanel('Your email domain matched your business website, so ownership was verified automatically.');
                    document.getElementById('owner-panel').scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else if (res.domain || res.contact_email || res.phone) {
                    newLabel.textContent = 'Verify ownership';
                    newBtn.classList.add('text-amber-600', 'border-amber-300', 'bg-amber-50');
                    newBtn.disabled = false;
                    // Open verify modal immediately
                    trackClaim('claim_verify_open', { claim_id: res.claim_id, has_email: Boolean(res.domain || res.contact_email), has_phone: Boolean(res.phone) });
                    showVerifyModal(res.claim_id, res.domain, res.contact_email, res.phone);
                    // Also allow re-opening on subsequent clicks
                    newBtn.onclick = () => {
                      trackClaim('claim_verify_open', { claim_id: res.claim_id, has_email: Boolean(res.domain || res.contact_email), has_phone: Boolean(res.phone) });
                      showVerifyModal(res.claim_id, res.domain, res.contact_email, res.phone);
                    };
                  } else {
                    newLabel.textContent = 'Claim pending';
                    newBtn.classList.add('text-amber-600', 'border-amber-300', 'bg-amber-50');
                  }
                } else {
                  trackClaim('claim_error', { category: claimErrorCategory(res.error), stage: 'create_claim' });
                  newLabel.textContent = res.error || 'Claim failed';
                  newBtn.classList.add('text-red-600');
                }
              })
              .catch(() => {
                trackClaim('claim_error', { category: 'network', stage: 'create_claim' });
                newLabel.textContent = 'Claim failed';
                newBtn.classList.add('text-red-600');
                newBtn.disabled = false;
              });
          });
        }
        if (onDone) onDone();
      })
      .catch(() => { if (onDone) onDone(); });
  };

  const showVerifyModal = (claimId, domain, contactEmail, phone) => {
    const modal = document.getElementById('claim-verify-modal');
    const desc = document.getElementById('claim-verify-desc');
    const emailInput = document.getElementById('claim-verify-email');
    const msg = document.getElementById('claim-verify-msg');
    const submitBtn = document.getElementById('claim-verify-btn');

    const hasEmail = domain || contactEmail;
    const hasPhone = !!phone;
    let mode = hasEmail ? 'email' : 'phone'; // default to email if available

    const setMode = (m) => {
      mode = m;
      msg.classList.add('hidden');
      submitBtn.disabled = false;
      if (m === 'email') {
        emailInput.type = 'email';
        emailInput.value = '';
        submitBtn.textContent = 'Send verification email';
        if (domain && contactEmail) {
          desc.textContent = `Enter an email address at @${domain} or the business contact email to verify ownership.`;
          emailInput.placeholder = `you@${domain}`;
        } else if (domain) {
          desc.textContent = `Enter an email address at @${domain} to verify you own this business.`;
          emailInput.placeholder = `you@${domain}`;
        } else if (contactEmail) {
          desc.textContent = 'Enter the business contact email address to verify you own this business.';
          emailInput.placeholder = 'Business contact email';
        }
      } else {
        desc.textContent = `We'll send a verification code to the business phone number ${phone}.`;
        emailInput.type = 'hidden';
        emailInput.value = '';
        submitBtn.textContent = 'Send verification code';
      }
      // Show/hide toggle links
      let toggleElInner = document.getElementById('claim-verify-toggle');
      if (toggleElInner) {
        if (hasEmail && hasPhone) {
          toggleElInner.classList.remove('hidden');
          toggleElInner.innerHTML = m === 'email'
            ? '<a href="#" class="text-xs text-blue-600 hover:underline">Verify by phone instead</a>'
            : '<a href="#" class="text-xs text-blue-600 hover:underline">Verify by email instead</a>';
          toggleElInner.querySelector('a').onclick = (ev) => { ev.preventDefault(); setMode(m === 'email' ? 'phone' : 'email'); };
        } else {
          toggleElInner.classList.add('hidden');
        }
      }
    };

    // Ensure toggle element exists
    let toggleEl = document.getElementById('claim-verify-toggle');
    if (!toggleEl) {
      toggleEl = document.createElement('p');
      toggleEl.id = 'claim-verify-toggle';
      toggleEl.className = 'mt-2 text-center hidden';
      msg.parentNode.insertBefore(toggleEl, msg.nextSibling);
    }

    setMode(hasEmail ? 'email' : 'phone');
    modal.classList.add('modal-open');

    document.getElementById('claim-verify-cancel').onclick = () => { modal.classList.remove('modal-open'); };
    document.getElementById('claim-verify-backdrop').onclick = () => { modal.classList.remove('modal-open'); };

    document.getElementById('claim-verify-form').onsubmit = (e) => {
      e.preventDefault();
      submitBtn.disabled = true;
      msg.classList.add('hidden');

      if (mode === 'email') {
        const email = emailInput.value.trim();
        if (!email) { submitBtn.disabled = false; return; }
        trackClaim('claim_verify_email_submit', { claim_id: claimId });
        submitBtn.textContent = 'Sending...';
        fetch(`${AUTH}/auth/claims/${claimId}/send-verification`, Object.assign({
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email })
        }, opts))
          .then(r => r.json())
          .then(d => {
            msg.classList.remove('hidden');
            if (d.ok) {
              trackClaim('claim_verification_email_requested', { claim_id: claimId });
              msg.className = 'text-sm mb-3 text-green-700';
              msg.textContent = d.message || 'Verification email sent!';
              submitBtn.textContent = 'Email sent';
            } else {
              trackClaim('claim_error', { claim_id: claimId, category: claimErrorCategory(d.error), stage: 'send_verification_email' });
              msg.className = 'text-sm mb-3 text-red-600';
              msg.textContent = d.error || 'Failed to send.';
              submitBtn.disabled = false; submitBtn.textContent = 'Send verification email';
            }
          })
          .catch(() => {
            trackClaim('claim_error', { claim_id: claimId, category: 'network', stage: 'send_verification_email' });
            msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600';
            msg.textContent = 'Network error.'; submitBtn.disabled = false; submitBtn.textContent = 'Send verification email';
          });
      } else {
        // Phone verification: send code, then show code input
        trackClaim('claim_phone_code_submit', { claim_id: claimId });
        submitBtn.textContent = 'Sending code...';
        fetch(`${AUTH}/auth/claims/${claimId}/send-phone-code`, Object.assign({
          method: 'POST', headers: { 'Content-Type': 'application/json' }
        }, opts))
          .then(r => r.json())
          .then(d => {
            if (d.ok) {
              trackClaim('claim_phone_code_requested', { claim_id: claimId, method: d.method || null });
              msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-green-700';
              msg.textContent = d.message || 'Code sent!';
              // Switch to code entry mode
              desc.textContent = 'Enter the 6-digit code:';
              emailInput.type = 'text';
              emailInput.value = '';
              emailInput.placeholder = '000000';
              emailInput.maxLength = 6;
              emailInput.pattern = '[0-9]{6}';
              emailInput.classList.remove('hidden');
              submitBtn.disabled = false;
              submitBtn.textContent = 'Verify code';
              if (toggleEl) toggleEl.classList.add('hidden');

              // Change form handler to verify code
              document.getElementById('claim-verify-form').onsubmit = (ev) => {
                ev.preventDefault();
                const code = emailInput.value.trim();
                if (!code || code.length !== 6) return;
                submitBtn.disabled = true; submitBtn.textContent = 'Verifying...';
                fetch(`${AUTH}/auth/claims/${claimId}/verify-phone-code`, Object.assign({
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ code: code })
                }, opts))
                  .then(r => r.json())
                  .then(d2 => {
                    msg.classList.remove('hidden');
                    if (d2.ok) {
                      trackClaim('claim_phone_verified', { claim_id: claimId });
                      msg.className = 'text-sm mb-3 text-green-700';
                      msg.textContent = d2.message || 'Verified!';
                      submitBtn.textContent = 'Verified';
                      setTimeout(() => { modal.classList.add('hidden'); location.reload(); }, 1500);
                    } else {
                      trackClaim('claim_error', { claim_id: claimId, category: claimErrorCategory(d2.error), stage: 'verify_phone_code' });
                      msg.className = 'text-sm mb-3 text-red-600';
                      msg.textContent = d2.error || 'Invalid code.';
                      submitBtn.disabled = false; submitBtn.textContent = 'Verify code';
                    }
                  })
                  .catch(() => {
                    trackClaim('claim_error', { claim_id: claimId, category: 'network', stage: 'verify_phone_code' });
                    msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600';
                    msg.textContent = 'Network error.'; submitBtn.disabled = false; submitBtn.textContent = 'Verify code';
                  });
              };
            } else {
              trackClaim('claim_error', { claim_id: claimId, category: claimErrorCategory(d.error), stage: 'send_phone_code' });
              msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600';
              msg.textContent = d.error || 'Failed to send code.';
              submitBtn.disabled = false; submitBtn.textContent = 'Send verification code';
            }
          })
          .catch(() => {
            trackClaim('claim_error', { claim_id: claimId, category: 'network', stage: 'send_phone_code' });
            msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600';
            msg.textContent = 'Network error.'; submitBtn.disabled = false; submitBtn.textContent = 'Send verification code';
          });
      }
    };
  };

  // ── Community Reviews ──
  const communityReviewCard = (rev) => {
    let photos = [];
    try { photos = typeof rev.photos === 'string' ? JSON.parse(rev.photos) : (rev.photos || []); } catch (e) { }

    let html = '<div class="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">';
    html += '<div class="flex items-center gap-3 p-4 pb-0">';
    if (rev.user_avatar) {
      html += `<img src="${escH(rev.user_avatar)}" class="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-slate-100" onerror="this.outerHTML='<div class=\\'w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0\\'>${escH((rev.user_name || '?').charAt(0).toUpperCase())}</div>'">`;
    } else {
      html += '<div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0">';
      html += escH((rev.user_name || '?').charAt(0).toUpperCase());
      html += '</div>';
    }
    html += '<div class="flex-1 min-w-0">';
    html += `<p class="font-semibold text-sm text-slate-900 truncate">${escH(rev.user_name || 'Anonymous')}</p>`;
    html += '<div class="flex items-center gap-2">';
    html += '<span class="flex gap-0.5">';
    for (let i = 0; i < 5; i++) {
      html += `<svg class="w-4 h-4 ${i < rev.rating ? 'text-amber-400' : 'text-slate-200'}" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>`;
    }
    html += '</span>';
    const dateStr = rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    html += `<span class="text-xs text-slate-400">${escH(dateStr)}</span>`;
    html += '</div></div></div>';
    html += '<div class="px-4 py-3">';
    html += `<p class="text-sm text-slate-700 leading-relaxed">${escH(rev.review_text)}</p>`;
    if (photos.length > 0) {
      html += '<div class="flex flex-wrap gap-2 mt-3">';
      photos.forEach(url => {
        html += `<a href="${escH(url)}" target="_blank" class="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200">`;
        html += `<img src="${escH(url)}" class="w-full h-full object-cover" loading="lazy">`;
        html += '</a>';
      });
      html += '</div>';
    }
    html += '</div></div>';
    return html;
  };

  const loadCommunityReviews = () => {
    fetch(`${AUTH}/auth/reviews?place_id=${encodeURIComponent(PLACE_ID)}`, opts)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.reviews || d.reviews.length === 0) return;
        let reviews = d.reviews;
        if (authUser && authUser.id) {
          reviews = reviews.filter(r => r.user_id !== authUser.id);
        }
        if (reviews.length === 0) return;
        const section = document.getElementById('community-reviews');
        const list = document.getElementById('community-list');
        if (!section || !list) return;
        section.style.display = '';
        list.innerHTML = reviews.map(communityReviewCard).join('');
      });
  };

  // ── Write Review ──
  let reviewPhotos = [];

  const initReviews = () => {
    const section = document.getElementById('write-review');
    const form = document.getElementById('review-form');
    if (!section || !form) return;
    section.style.display = '';

    fetch(`${AUTH}/auth/reviews?place_id=${encodeURIComponent(PLACE_ID)}&mine=1`, opts)
      .then(r => r.json())
      .then(d => {
        if (d.reviewed && d.review) {
          form.style.display = 'none';
          document.getElementById('write-review-title').textContent = 'Write a Review';
          showMyReviewCard(d.review);
        }
      });

    const starSelect = document.getElementById('star-select');
    const ratingInput = document.getElementById('review-rating');
    if (starSelect) {
      starSelect.querySelectorAll('[data-star]').forEach(star => {
        star.addEventListener('click', () => {
          const val = parseInt(star.dataset.star);
          ratingInput.value = val;
          starSelect.querySelectorAll('[data-star]').forEach(s => {
            s.style.color = parseInt(s.dataset.star) <= val ? '#f59e0b' : '#cbd5e1';
          });
        });
      });
    }

    const textarea = document.getElementById('review-text');
    const charCount = document.getElementById('review-chars');
    if (textarea && charCount) {
      textarea.addEventListener('input', () => { charCount.textContent = textarea.value.length; });
    }

    const photoInput = document.getElementById('review-photo-input');
    if (photoInput) {
      photoInput.addEventListener('change', () => {
        const files = Array.from(photoInput.files || []);
        files.forEach(file => {
          if (reviewPhotos.length >= 5) return;
          if (file.size > 10 * 1024 * 1024) { showReviewMsg('Image too large (max 10MB).', 'red'); return; }
          reviewPhotos.push({ file: file, cdnUrl: null, uploading: false });
          renderReviewPhotos();
        });
        photoInput.value = '';
      });
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const rating = parseInt(ratingInput.value);
      if (rating < 1 || rating > 5) { showReviewMsg('Please select a rating.', 'red'); return; }
      const text = textarea.value.trim();
      if (text.length < 10) { showReviewMsg('Review must be at least 10 characters.', 'red'); return; }

      const btn = document.getElementById('review-submit');
      btn.disabled = true; btn.textContent = 'Uploading photos...';

      uploadReviewPhotos((photoUrls) => {
        const isEditing = !!window._editingReviewId;
        btn.textContent = isEditing ? 'Updating...' : 'Submitting...';
        const url = isEditing ? `${AUTH}/auth/reviews/${window._editingReviewId}` : `${AUTH}/auth/reviews`;
        const method = isEditing ? 'PATCH' : 'POST';
        fetch(url, Object.assign({
          method: method, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ place_id: PLACE_ID, rating: rating, review_text: text, business_name: BIZ_NAME, photos: photoUrls })
        }, opts))
          .then(r => r.json())
          .then(d => {
            btn.disabled = false; btn.textContent = isEditing ? 'Update Review' : 'Submit Review';
            if (d.ok) {
              showReviewMsg(isEditing ? 'Review updated!' : 'Review submitted! Thank you.', 'green');
              form.style.display = 'none';
              showMyReviewCard({
                id: isEditing ? window._editingReviewId : d.review_id,
                rating: rating, review_text: text, photos: JSON.stringify(photoUrls),
                created_at: new Date().toISOString()
              });
              window._editingReviewId = null;
              reviewPhotos = [];
              loadCommunityReviews();
            } else {
              showReviewMsg(d.error || 'Failed to submit review.', 'red');
            }
          })
          .catch(() => {
            btn.disabled = false; btn.textContent = isEditing ? 'Update Review' : 'Submit Review';
            showReviewMsg('Network error. Please try again.', 'red');
          });
      });
    });
  };

  const renderReviewPhotos = () => {
    const container = document.getElementById('review-photos');
    if (!container) return;
    let html = '';
    reviewPhotos.forEach((p, idx) => {
      if (!p.cdnUrl && !p._blobUrl) p._blobUrl = URL.createObjectURL(p.file);
      const src = p.cdnUrl || p._blobUrl;
      html += '<div class="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">';
      html += `<img src="${src}" class="w-full h-full object-cover">`;
      html += `<button type="button" data-photo-idx="${idx}" class="review-photo-remove absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 text-white rounded-full text-xs flex items-center justify-center hover:bg-black/80">&times;</button>`;
      html += '</div>';
    });
    container.innerHTML = html;
    container.querySelectorAll('.review-photo-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-photo-idx'));
        const removed = reviewPhotos.splice(idx, 1)[0];
        if (removed && removed._blobUrl) URL.revokeObjectURL(removed._blobUrl);
        renderReviewPhotos();
      });
    });
    const msg = document.getElementById('review-photo-msg');
    if (msg) msg.textContent = reviewPhotos.length > 0 ? `${reviewPhotos.length}/5 photos` : '';
  };

  const uploadReviewPhotos = (callback) => {
    const pending = reviewPhotos.filter(p => !p.cdnUrl);
    if (pending.length === 0) {
      callback(reviewPhotos.map(p => p.cdnUrl).filter(Boolean));
      return;
    }

    const container = document.getElementById('review-photos');
    let done = 0;
    pending.forEach(p => {
      const progress = createProgressBar(container || document.getElementById('review-form'), p.file);
      fetch(`${AUTH}/auth/reviews/upload`, Object.assign({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: p.file.type, place_id: PLACE_ID })
      }, opts))
        .then(r => r.json())
        .then(d => {
          if (!d.ok) { progress.error('Upload failed'); finish(); return; }
          return uploadWithProgress(d.upload_url, p.file, p.file.type, (pct) => {
            progress.update(pct);
          }).then(() => {
            p.cdnUrl = d.cdn_url;
            progress.done(d.cdn_url);
            finish();
          });
        })
        .catch(() => { progress.error('Network error'); finish(); });
    });

    const finish = () => {
      done++;
      if (done >= pending.length) {
        callback(reviewPhotos.map(p => p.cdnUrl).filter(Boolean));
      }
    };
  };

  const showReviewMsg = (text, color) => {
    const msg = document.getElementById('review-msg');
    if (!msg) return;
    msg.classList.remove('hidden');
    msg.className = `text-sm ${color === 'green' ? 'text-green-700' : 'text-red-600'}`;
    msg.textContent = text;
  };

  const showMyReviewCard = (rev) => {
    const slot = document.getElementById('my-review-slot');
    if (!slot) return;
    const section = document.getElementById('community-reviews');
    if (section) section.style.display = '';

    let photos = [];
    try { photos = typeof rev.photos === 'string' ? JSON.parse(rev.photos) : (rev.photos || []); } catch (e) { }

    let html = '<div class="bg-blue-50 border border-blue-200 rounded-xl shadow-sm overflow-hidden mb-3">';
    html += '<div class="flex items-center gap-3 p-4 pb-0">';
    if (authUser && (authUser.avatar_url || authUser.avatar)) {
      html += `<img src="${escH(authUser.avatar_url || authUser.avatar)}" class="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-slate-100">`;
    } else {
      html += '<div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0">';
      html += escH(((authUser && authUser.name) || (authUser && authUser.email) || '?').charAt(0).toUpperCase());
      html += '</div>';
    }
    html += '<div class="flex-1 min-w-0">';
    html += '<div class="flex items-center gap-2">';
    html += `<p class="font-semibold text-sm text-slate-900 truncate">${escH((authUser && authUser.name) || 'You')}</p>`;
    html += '<span class="text-xs font-medium text-blue-600 bg-blue-100 rounded-full px-2 py-0.5">Your review</span>';
    html += '</div>';
    html += '<div class="flex items-center gap-2">';
    html += '<span class="flex gap-0.5">';
    for (let i = 0; i < 5; i++) {
      html += `<svg class="w-4 h-4 ${i < rev.rating ? 'text-amber-400' : 'text-slate-200'}" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>`;
    }
    html += '</span>';
    const dateStr = rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    html += `<span class="text-xs text-slate-400">${escH(dateStr)}</span>`;
    html += '</div></div></div>';
    html += '<div class="px-4 py-3">';
    html += `<p class="text-sm text-slate-700 leading-relaxed">${escH(rev.review_text)}</p>`;
    if (photos.length > 0) {
      html += '<div class="flex flex-wrap gap-2 mt-3">';
      photos.forEach(url => {
        html += `<a href="${escH(url)}" target="_blank" class="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200">`;
        html += `<img src="${escH(url)}" class="w-full h-full object-cover" loading="lazy">`;
        html += '</a>';
      });
      html += '</div>';
    }
    html += '<div class="flex items-center gap-4 mt-3 pt-3 border-t border-blue-100">';
    html += '<button type="button" id="edit-review-btn" class="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">Edit</button>';
    html += '<button type="button" id="delete-review-btn" class="text-xs font-medium text-red-500 hover:text-red-600 transition-colors">Delete</button>';
    html += '</div>';
    html += '</div></div>';
    slot.innerHTML = html;

    document.getElementById('edit-review-btn').addEventListener('click', () => {
      slot.innerHTML = '';
      document.getElementById('write-review-title').textContent = 'Edit Review';
      const form = document.getElementById('review-form');
      form.style.display = '';
      document.getElementById('review-rating').value = rev.rating;
      document.getElementById('star-select').querySelectorAll('[data-star]').forEach(s => {
        s.style.color = parseInt(s.dataset.star) <= rev.rating ? '#f59e0b' : '#cbd5e1';
      });
      document.getElementById('review-text').value = rev.review_text;
      document.getElementById('review-chars').textContent = rev.review_text.length;
      reviewPhotos = photos.map(url => ({ cdnUrl: url, file: null }));
      renderReviewPhotos();
      window._editingReviewId = rev.id;
      document.getElementById('review-submit').textContent = 'Update Review';
    });

    document.getElementById('delete-review-btn').addEventListener('click', () => {
      showConfirm('Delete your review?', { confirmText: 'Delete' }).then(ok => {
        if (!ok) return;
        fetch(`${AUTH}/auth/reviews/${rev.id}`, Object.assign({ method: 'DELETE' }, opts))
          .then(r => r.json())
          .then(d => {
            if (d.ok) {
              slot.innerHTML = '';
              document.getElementById('write-review-title').textContent = 'Write a Review';
              document.getElementById('review-form').style.display = '';
              resetReviewForm();
              loadCommunityReviews();
            }
          });
      });
    });
  };

  const resetReviewForm = () => {
    document.getElementById('review-rating').value = '0';
    document.getElementById('review-text').value = '';
    document.getElementById('review-chars').textContent = '0';
    document.getElementById('star-select').querySelectorAll('[data-star]').forEach(s => { s.style.color = '#cbd5e1'; });
    reviewPhotos = [];
    renderReviewPhotos();
    window._editingReviewId = null;
    document.getElementById('review-submit').textContent = 'Submit Review';
  };

  // ── Owner Manage Banner ──

  const showOwnerBanner = () => {
    if (!isOwner) return;
    const banner = document.getElementById('owner-manage-banner');
    const link = document.getElementById('owner-manage-link');
    if (banner) banner.style.display = '';
    if (link) link.href = `/account/#/business/${encodeURIComponent(PLACE_ID)}`;
  };

  // ── Load owner photos + filter hidden images from gallery ──
  const loadOwnerPhotosIntoGallery = () => {
    // First, fetch hidden images and remove them from the gallery
    fetch(`${AUTH}/auth/business/${encodeURIComponent(PLACE_ID)}/hidden-images`, opts)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.urls && d.urls.length > 0) {
          const strip = document.querySelector('.photo-strip');
          if (strip) {
            d.urls.forEach(url => {
              const img = strip.querySelector(`img[src="${url}"]`);
              if (img && img.parentElement) img.parentElement.remove();
            });
          }
        }
      })
      .catch(() => {});

    // Then, append owner-uploaded photos
    fetch(`${AUTH}/auth/business/${encodeURIComponent(PLACE_ID)}/photos`, opts)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.photos || d.photos.length === 0) return;
        let strip = document.querySelector('.photo-strip');
        if (!strip) {
          const main = document.querySelector('main');
          if (!main) return;
          const section = document.createElement('section');
          section.innerHTML = `<h2 class="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Gallery</h2><div class="strip-wrap relative"><div class="photo-strip flex gap-2 overflow-x-auto"></div></div>`;
          main.insertBefore(section, main.firstChild.nextSibling);
          strip = section.querySelector('.photo-strip');
          if (!strip) return;
        }
        d.photos.forEach(p => {
          if (strip.querySelector(`img[src="${p.url}"]`)) return;
          const a = document.createElement('a');
          a.href = p.url;
          a.target = '_blank';
          a.rel = 'noopener';
          a.className = 'flex-shrink-0 block';
          a.innerHTML = `<img src="${escH(p.url)}" alt="${escH(BIZ_NAME)} photo" class="h-32 w-48 object-cover rounded-xl border border-slate-200 hover:opacity-85 hover:border-blue-300 transition-all duration-150" loading="lazy" draggable="false" onerror="this.parentElement.remove()">`;
          strip.appendChild(a);
        });
        const section = strip.closest('section');
        if (section) section.style.display = '';
      })
      .catch(() => {});
  };

  // ── Public Features: Badges, Booking, SMS, Financing, Quote, Portfolio ──
  (() => {
    // First check if business is claimed, then load settings
    fetch(`${AUTH}/auth/claims/check?place_id=${encodeURIComponent(PLACE_ID)}`, opts)
      .then(r => r.json())
      .then(claimData => {
        if (!claimData.claimed) {
          // Not claimed — no owner features to show
          return;
        }
        // Business is claimed — load public settings
        fetch(`${AUTH}/auth/business/${encodeURIComponent(PLACE_ID)}/public-settings`, opts)
          .then(r => r.json())
          .then(d => {
            const s = (d.ok && d.settings) ? d.settings : {};
            renderBadges(s.badges);
            renderActionButtons(s);
            loadPortfolio();
            loadOwnerPhotosIntoGallery();
          })
          .catch(() => {
            renderActionButtons({});
            loadPortfolio();
          });
      });
  })();

  const renderBadges = (badgesJson) => {
    if (!badgesJson) return;
    let badges;
    try { badges = JSON.parse(badgesJson); } catch (e) { return; }
    if (!badges || !badges.length) return;

    const svg = (d) => `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
    const badgeLabels = {
      licensed_insured: { label: 'Licensed & Insured', icon: svg('<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>'), color: 'bg-green-100 text-green-800 border-green-200' },
      bonded: { label: 'Bonded', icon: svg('<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>'), color: 'bg-purple-100 text-purple-800 border-purple-200' },
      veteran_owned: { label: 'Veteran Owned', icon: svg('<circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>'), color: 'bg-red-100 text-red-800 border-red-200' },
      family_owned: { label: 'Family Owned', icon: svg('<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'), color: 'bg-orange-100 text-orange-800 border-orange-200' },
      eco_friendly: { label: 'Eco-Friendly', icon: svg('<path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 20 .5 20 .5s-4.7 5-2.7 11.2A7 7 0 0 1 11 20"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>'), color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
      free_estimates: { label: 'Free Estimates', icon: svg('<line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>'), color: 'bg-teal-100 text-teal-800 border-teal-200' }
    };

    const bar = document.getElementById('biz-badges-bar');
    const container = document.getElementById('biz-badges');
    if (!bar || !container) return;

    let html = '';
    badges.forEach(key => {
      const b = badgeLabels[key];
      if (!b) return;
      html += `<span class="badge badge-sm badge-outline gap-1 ${b.color}">`;
      html += `${b.icon} ${escH(b.label)}`;
      html += '</span>';
    });

    if (html) {
      container.innerHTML = html;
      bar.style.display = '';
    }
  };

  const renderActionButtons = (settings) => {
    const bar = document.getElementById('biz-actions-bar');
    const container = document.getElementById('biz-actions');
    if (!bar || !container) return;

    let html = '';

    // Quote button (always show for claimed businesses)
    html += '<button id="quote-btn" class="btn btn-success btn-sm">';
    html += '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"/></svg>';
    html += 'Get a Free Quote';
    html += '</button>';

    // Booking button (Feature 12)
    if (settings.booking_url && settings.booking_provider) {
      const providerLabel = { housecall_pro: 'Housecall Pro', jobber: 'Jobber', custom: 'Online' }[settings.booking_provider] || 'Online';
      html += `<a href="${escH(settings.booking_url)}" target="_blank" rel="noopener" class="btn btn-primary btn-sm">`;
      html += '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/></svg>';
      html += `Book ${escH(providerLabel)}`;
      html += '</a>';
    } else {
      // Built-in booking (no external URL)
      html += '<button id="booking-btn" class="btn btn-primary btn-sm">';
      html += '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/></svg>';
      html += 'Book Appointment';
      html += '</button>';
    }

    // SMS button (Feature 15) — disabled, requires Twilio
    // if (settings.sms_enabled === 'on') { ... }

    // Financing badge (Feature 16)
    if (settings.financing_enabled === 'on' && settings.financing_url) {
      html += `<a href="${escH(settings.financing_url)}" target="_blank" rel="noopener" class="badge badge-sm badge-success badge-outline gap-1">`;
      html += '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"/></svg>';
      html += 'Financing Available';
      html += '</a>';
    }

    if (html) {
      container.innerHTML = html;
      bar.style.display = '';

      // Attach handlers
      const quoteBtn = document.getElementById('quote-btn');
      if (quoteBtn) {
        quoteBtn.addEventListener('click', () => {
          document.getElementById('quote-modal').showModal();
        });
      }
      // SMS button disabled — requires Twilio
      const bookingBtn = document.getElementById('booking-btn');
      if (bookingBtn) {
        bookingBtn.addEventListener('click', () => {
          document.getElementById('booking-modal').showModal();
        });
      }
    }
  };

  // ── Quote form (Feature 11) ──
  const quoteForm = document.getElementById('quote-form');
  if (quoteForm) {
    quoteForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = document.getElementById('quote-submit');
      const msg = document.getElementById('quote-msg');
      btn.disabled = true; btn.textContent = 'Sending...';
      msg.classList.add('hidden');

      fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          place_id: PLACE_ID,
          business_name: BIZ_NAME,
          name: document.getElementById('quote-name').value.trim(),
          email: document.getElementById('quote-email').value.trim(),
          phone: document.getElementById('quote-phone').value.trim() || null,
          project_description: document.getElementById('quote-desc').value.trim()
        })
      })
        .then(r => r.json())
        .then(d => {
          btn.disabled = false; btn.textContent = 'Send Request';
          msg.classList.remove('hidden');
          if (d.ok) {
            msg.className = 'text-sm text-green-700';
            msg.textContent = 'Your quote request has been sent! The business will contact you soon.';
            quoteForm.reset();
            setTimeout(() => { document.getElementById('quote-modal').close(); }, 2000);
          } else {
            msg.className = 'text-sm text-red-600';
            msg.textContent = d.error || 'Failed to send request.';
          }
        })
        .catch(() => {
          btn.disabled = false; btn.textContent = 'Send Request';
          msg.classList.remove('hidden'); msg.className = 'text-sm text-red-600';
          msg.textContent = 'Network error. Please try again.';
        });
    });
  }

  // SMS form (Feature 15) — disabled, requires Twilio

  // ── Booking form ──
  const bookingForm = document.getElementById('booking-form');
  if (bookingForm) {
    // Set min date to today
    const dateInput = document.getElementById('booking-date');
    if (dateInput) dateInput.min = new Date().toISOString().split('T')[0];

    bookingForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = document.getElementById('booking-submit');
      const msg = document.getElementById('booking-msg');
      btn.disabled = true; btn.textContent = 'Booking...';
      msg.classList.add('hidden');

      fetch(`${AUTH}/auth/tools/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          place_id: PLACE_ID,
          business_name: BIZ_NAME,
          customer_name: document.getElementById('booking-name').value.trim(),
          customer_email: document.getElementById('booking-email').value.trim(),
          customer_phone: document.getElementById('booking-phone').value.trim() || null,
          service_type: document.getElementById('booking-service').value.trim() || null,
          preferred_date: document.getElementById('booking-date').value || null,
          preferred_time: document.getElementById('booking-time').value || null,
          notes: document.getElementById('booking-notes').value.trim() || null,
        })
      })
        .then(r => r.json())
        .then(d => {
          btn.disabled = false; btn.textContent = 'Book Appointment';
          msg.classList.remove('hidden');
          if (d.ok) {
            msg.className = 'text-sm text-green-700';
            msg.textContent = 'Appointment request sent! The business will confirm your booking.';
            bookingForm.reset();
            setTimeout(() => { document.getElementById('booking-modal').close(); }, 2500);
          } else {
            msg.className = 'text-sm text-red-600';
            msg.textContent = d.error || 'Failed to book.';
          }
        })
        .catch(() => {
          btn.disabled = false; btn.textContent = 'Book Appointment';
          msg.classList.remove('hidden'); msg.className = 'text-sm text-red-600';
          msg.textContent = 'Network error. Please try again.';
        });
    });
  }

  // ── Portfolio display (Feature 17) ──
  const loadPortfolio = () => {
    fetch(`${AUTH}/auth/business/${encodeURIComponent(PLACE_ID)}/portfolio`, opts)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.projects || d.projects.length === 0) return;
        const section = document.getElementById('portfolio-section');
        const grid = document.getElementById('portfolio-grid');
        if (!section || !grid) return;

        let html = '';
        d.projects.forEach(p => {
          html += '<div class="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">';
          if (p.before_photo_url || p.after_photo_url) {
            html += '<div class="grid grid-cols-2 gap-px bg-slate-200">';
            if (p.before_photo_url) {
              html += `<div class="relative bg-white"><img src="${escH(p.before_photo_url)}" class="w-full h-36 object-cover" loading="lazy">`;
              html += '<span class="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px] font-semibold rounded">BEFORE</span></div>';
            } else {
              html += '<div class="bg-slate-100 flex items-center justify-center h-36 text-xs text-slate-400">No before photo</div>';
            }
            if (p.after_photo_url) {
              html += `<div class="relative bg-white"><img src="${escH(p.after_photo_url)}" class="w-full h-36 object-cover" loading="lazy">`;
              html += '<span class="absolute bottom-1 left-1 px-1.5 py-0.5 bg-green-600 text-white text-[10px] font-semibold rounded">AFTER</span></div>';
            } else {
              html += '<div class="bg-slate-100 flex items-center justify-center h-36 text-xs text-slate-400">No after photo</div>';
            }
            html += '</div>';
          }
          html += '<div class="p-3">';
          html += `<h4 class="font-semibold text-sm text-slate-900">${escH(p.title)}</h4>`;
          if (p.description) html += `<p class="text-xs text-slate-500 mt-1">${escH(p.description)}</p>`;
          html += '</div></div>';
        });

        grid.innerHTML = html;
        section.style.display = '';
      })
      .catch(() => { /* ignore */ });
  };

  // ── Removal Request ──
  const removalLink = document.getElementById('removal-link');
  if (removalLink) {
    removalLink.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('removal-modal').showModal();
    });
  }

  const removalForm = document.getElementById('removal-form');
  if (removalForm) {
    removalForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = e.currentTarget.querySelector('button[type="submit"]');
      const msg = document.getElementById('removal-msg');
      const email = document.getElementById('removal-email').value;
      btn.disabled = true; btn.textContent = 'Sending...';
      msg.className = 'text-sm mb-3 text-slate-500'; msg.textContent = 'Sending...'; msg.classList.remove('hidden');
      fetch('/removal/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: BIZ_SLUG, email: email })
      }).then(r => r.json()).then(d => {
        btn.disabled = false; btn.textContent = 'Send Verification Email';
        if (d.ok) {
          msg.className = 'text-sm mb-3 text-green-700';
          msg.textContent = d.message || 'Verification email sent. Check your inbox.';
        } else {
          msg.className = 'text-sm mb-3 text-red-600';
          msg.textContent = d.error || 'Request failed.';
        }
      }).catch(() => {
        btn.disabled = false; btn.textContent = 'Send Verification Email';
        msg.className = 'text-sm mb-3 text-red-600';
        msg.textContent = 'Network error. Please try again.';
      });
    });
  }
})();
